// Block-modell för designläge-byggaren.
// Hela mailet representeras som en sekvens av Block-instanser som
// kan serialiseras till och parsas från email-säker HTML.

export type BlockId = string;

export type BlockType = 'heading' | 'text' | 'image' | 'twoColumns' | 'button' | 'divider';

export type HeadingLevel = 1 | 2 | 3;
export type TextAlign = 'left' | 'center' | 'right';

export interface BaseBlock {
  id: BlockId;
  type: BlockType;
}

export interface HeadingBlock extends BaseBlock {
  type: 'heading';
  level: HeadingLevel;
  text: string;
  align: TextAlign;
}

export interface TextBlock extends BaseBlock {
  type: 'text';
  // TinyMCE-output, behandlas som trusted HTML.
  html: string;
}

export interface ImageBlock extends BaseBlock {
  type: 'image';
  src: string;
  alt: string;
  width?: number;
  href?: string;
  // True = bilden renderas kantlöst utan inramning (padding: 0 i td). Annars
  // 12px 30px (standard) så bilden ligger inom content-kolumnens marginaler.
  fullBleed?: boolean;
}

export interface TwoColumnsBlock extends BaseBlock {
  type: 'twoColumns';
  left: Block[];
  right: Block[];
}

export interface ButtonBlock extends BaseBlock {
  type: 'button';
  label: string;
  href: string;
  align: TextAlign;
  bgColor?: string;
  textColor?: string;
}

export interface DividerBlock extends BaseBlock {
  type: 'divider';
  color?: string;
}

export type Block = HeadingBlock | TextBlock | ImageBlock | TwoColumnsBlock | ButtonBlock | DividerBlock;

// Dokument-nivå inställningar. Speglar envelope-mönstret i system-mallarna —
// varje block läggs som en <tr> där body-färgen (yttre bakgrund) ramar in en
// 600px-bred content-kolumn med content-färgen.
export interface EmailDocSettings {
  bodyBgColor: string;
  contentBgColor: string;
  // Font-family stack för rubriker respektive brödtext. Default matchar
  // system-mallarna (Helvetica/Arial).
  headingFontFamily: string;
  bodyFontFamily: string;
}

export const DEFAULT_FONT_STACK = 'Helvetica, Arial, sans-serif';

// Lista över font-stackar som visas i typsnitt-väljaren. Moderna web-fonts
// (Google Fonts, Poppins/Inter/etc.) först — om mail-klienten stödjer @import
// laddas de in via envelope-style. Fallback-system fonts gör så att äldre
// klienter (Outlook m.fl.) renderar något läsbart.
//
// `googleFamily` är namnet som ska skickas till Google Fonts CSS-API — null
// när stacken bara använder system-fonts.
export const AVAILABLE_FONT_STACKS: { label: string; stack: string; googleFamily: string | null }[] = [
  { label: 'Inter', stack: '"Inter", "Helvetica Neue", Helvetica, Arial, sans-serif', googleFamily: 'Inter:wght@400;600;700' },
  { label: 'Poppins', stack: '"Poppins", "Helvetica Neue", Helvetica, Arial, sans-serif', googleFamily: 'Poppins:wght@400;600;700' },
  { label: 'Roboto', stack: '"Roboto", "Helvetica Neue", Helvetica, Arial, sans-serif', googleFamily: 'Roboto:wght@400;500;700' },
  { label: 'Open Sans', stack: '"Open Sans", "Helvetica Neue", Helvetica, Arial, sans-serif', googleFamily: 'Open+Sans:wght@400;600;700' },
  { label: 'Montserrat', stack: '"Montserrat", "Helvetica Neue", Helvetica, Arial, sans-serif', googleFamily: 'Montserrat:wght@400;600;700' },
  { label: 'Lato', stack: '"Lato", "Helvetica Neue", Helvetica, Arial, sans-serif', googleFamily: 'Lato:wght@400;700;900' },
  { label: 'Nunito', stack: '"Nunito", "Helvetica Neue", Helvetica, Arial, sans-serif', googleFamily: 'Nunito:wght@400;600;700' },
  { label: 'Playfair Display', stack: '"Playfair Display", Georgia, "Times New Roman", serif', googleFamily: 'Playfair+Display:wght@400;700' },
  { label: 'Merriweather', stack: '"Merriweather", Georgia, "Times New Roman", serif', googleFamily: 'Merriweather:wght@400;700' },
  { label: 'Arial (system)', stack: 'Arial, Helvetica, sans-serif', googleFamily: null },
  { label: 'Helvetica (system)', stack: 'Helvetica, Arial, sans-serif', googleFamily: null },
  { label: 'Georgia (system)', stack: 'Georgia, "Times New Roman", serif', googleFamily: null },
  { label: 'Times New Roman (system)', stack: '"Times New Roman", Times, serif', googleFamily: null },
];

// Bygger en Google Fonts URL för de fonter som faktiskt används i utskicket.
// Returnerar null om alla valda stackar är system-fonts.
export const buildGoogleFontsUrl = (families: (string | null)[]): string | null => {
  const used = Array.from(new Set(families.filter((f): f is string => !!f)));
  if (used.length === 0) return null;
  const familyParams = used.map((f) => `family=${f}`).join('&');
  return `https://fonts.googleapis.com/css2?${familyParams}&display=swap`;
};

// Slår upp Google Fonts-familjenamnet för en given font-stack — används av
// serializern för att veta vilka @import-anrop som ska in i envelope-style.
export const findGoogleFamily = (stack: string): string | null => {
  const entry = AVAILABLE_FONT_STACKS.find((f) => f.stack === stack);
  return entry?.googleFamily ?? null;
};

export const DEFAULT_DOC_SETTINGS: EmailDocSettings = {
  bodyBgColor: '#eeeeee',
  contentBgColor: '#ffffff',
  headingFontFamily: DEFAULT_FONT_STACK,
  bodyFontFamily: DEFAULT_FONT_STACK,
};

export const CONTENT_MAX_WIDTH_PX = 600;

// Horisontell padding mellan content-kolumn och kant (body-bg syns på sidorna).
// Vertikala spacers ger luft ovanför första och under sista blocket.
export const ENVELOPE_GUTTER_PX = 20;
export const ENVELOPE_TOP_SPACER_PX = 20;
export const ENVELOPE_BOTTOM_SPACER_PX = 30;

// Genererar ett nytt block-id. Använder crypto.randomUUID när det finns,
// annars en enkel fallback baserad på Math.random (uuid-paketet är inte
// installerat i denna app).
export const newBlockId = (): BlockId => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `b-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
};

// Klonar ett block rekursivt och tilldelar nya id:n hela vägen ner.
// Används vid duplicering — krävs så att två kopior av ett block (inklusive
// nested twoColumns-innehåll) inte delar referenser eller id.
export const cloneBlock = (block: Block): Block => {
  const baseId = newBlockId();
  switch (block.type) {
    case 'twoColumns':
      return {
        ...block,
        id: baseId,
        left: block.left.map(cloneBlock),
        right: block.right.map(cloneBlock),
      };
    default:
      return { ...block, id: baseId };
  }
};

// Skapar ett nytt block med rimliga default-värden per typ.
export const createBlock = (type: BlockType): Block => {
  const id = newBlockId();
  switch (type) {
    case 'heading':
      return { id, type: 'heading', level: 2, text: 'Rubrik', align: 'left' };
    case 'text':
      return { id, type: 'text', html: '<p>Skriv din text här...</p>' };
    case 'image':
      return {
        id,
        type: 'image',
        src: 'https://placehold.co/600x260/d8d8d8/888888?text=Bild',
        alt: '',
      };
    case 'twoColumns':
      return { id, type: 'twoColumns', left: [], right: [] };
    case 'button':
      return {
        id,
        type: 'button',
        label: 'Läs mer',
        href: '#',
        align: 'center',
        bgColor: '#222222',
        textColor: '#ffffff',
      };
    case 'divider':
      return { id, type: 'divider', color: '#dddddd' };
    default: {
      // Bör aldrig hända — exhaustiveness-check.
      const exhaustive: never = type;
      throw new Error(`Okänd blocktyp: ${exhaustive as string}`);
    }
  }
};
